import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type {
  Metric,
  Newsletter as ApiNewsletter,
  NewslettersNewsletterScheduleResponse,
  NewslettersNewsletterSendTiming,
  NewslettersNewsletterTemplatesResponse,
  NewslettersPacingResponse,
  NewslettersSendStatusResponse,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { toWireIds } from '../shared/wire-ids';
import { requireWorkspaceId } from '../shared/workspace';
import {
  newsletterCatalogKey,
  newsletterKeys,
  newsletterMetricKey,
  newsletterMetricsKey,
  newsletterTemplateMetricsKey,
  newsletterTrackedLinksKey,
  templateTrackedLinksKey,
  newsletterTrackedResponseMetricsKey,
  newsletterScheduleKey,
  newsletterSendStatusKey,
  parseNewsletter,
  type Newsletter,
  type NewsletterCatalogScope,
  type NewsletterListParams,
  type NewsletterMetricsPeriod,
  type TemplateTrackedLinksQuery,
} from './entity';
import { isSent } from './selectors';

const STALE_TIME = 60_000;

/** The newsletters list plus the workspace total (pagination meta or page length). */
export interface NewslettersList {
  newsletters: Newsletter[];
  total: number;
}

/** Parses a list response's rows and sideloads each into its detail slot. */
function sideloadNewsletters(workspaceId: number, rows: ApiNewsletter[] | undefined, qc: QueryClient): Newsletter[] {
  const newsletters = (rows ?? []).map(parseNewsletter);
  for (const newsletterRow of newsletters) {
    qc.setQueryData(newsletterKeys.detail(workspaceId, newsletterRow.id), newsletterRow);
  }
  return newsletters;
}

/**
 * Workspace newsletters (broadcasts) list. Each row is sideloaded into its
 * detail slot so a follow-up `useNewsletterOptional` resolves from cache.
 * `total` prefers the pagination meta and falls back to the page length.
 *
 * @public
 */
export async function getNewsletters(workspaceId: number, qc: QueryClient): Promise<NewslettersList> {
  const raw = await hydraUiApi.newsletters.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const newsletters = sideloadNewsletters(workspaceId, raw.newsletters, qc);
  return { newsletters, total: raw.meta?.pagination?.total ?? newsletters.length };
}

/** @public */
export const getNewslettersQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: newsletterKeys.list(workspaceId, undefined),
    queryFn: () => getNewsletters(workspaceId, qc),
    staleTime: STALE_TIME,
  });

type NewslettersListQuery = NonNullable<
  NonNullable<NonNullable<Parameters<typeof hydraUiApi.newsletters.list>[0]>['params']>['query']
>;

/* ------------------------------------------------------------------------- */
/* Broadcasts newsletters list — filtered, non-cached                         */
/* ------------------------------------------------------------------------- */

/**
 * The per-status counts the list response carries alongside the page.
 *
 * These count the *current* filter set, not the workspace: the handler
 * recomputes them after applying the date filter, so they move with the
 * selected range. `all_time` is the exception, and is what the welcome empty
 * state keys off.
 */
export interface NewsletterTotals {
  sent: number;
  drafted: number;
  scheduled: number;
  all_time: number;
}

/** One page of the filtered newsletters list, with the server's meta. */
export interface NewslettersPage {
  newsletters: Newsletter[];
  pagination: { page: number; size: number; total: number };
  totals: NewsletterTotals;
  /**
   * When this page was read, in epoch seconds: the "now" its rows' states are judged against.
   * It travels with the page, so rows still on screen while the next page loads keep their own.
   */
  readAt: number;
}

function toNewslettersListQuery(params: NewsletterListParams): NewslettersListQuery {
  return {
    page: params.page - 1, // 1-based at the hook boundary, 0-based on the wire
    channel: params.channel,
    type: params.type,
    search: params.search,
    sort: params.sort,
    asc: params.asc,
    start_time: params.startTime,
    end_time: params.endTime,
    sendToUnsubscribed: params.sendToUnsubscribed,
    subscriptionTopicId: params.subscriptionTopicId,
    'tagIds[]': toWireIds(params.tagIds),
    'objectTypeIds[]': toWireIds(params.objectTypeIds),
    'goalIds[]': toWireIds(params.goalIds),
  };
}

/**
 * One page of the Broadcasts newsletters list.
 *
 * Every row is sideloaded into its detail slot, so a follow-up
 * `useNewsletterOptional` resolves from cache without a second request.
 *
 * @public
 */
export async function getFilteredNewsletters(
  workspaceId: number,
  params: NewsletterListParams,
  qc: QueryClient,
): Promise<NewslettersPage> {
  const raw = await hydraUiApi.newsletters.list({
    params: { path: { environment_id: String(workspaceId) }, query: toNewslettersListQuery(params) },
  });
  const newsletters = sideloadNewsletters(workspaceId, raw.newsletters, qc);
  return {
    newsletters,
    // Read as-is: the handler always writes both, and the spec marks them
    // required. A fallback could only invent numbers, and a zeroed `all_time`
    // is exactly what reads as "no newsletters yet" to the welcome state.
    // "Unknown" is the page's call, from a pending or failed query.
    pagination: raw.meta.pagination,
    totals: raw.meta.totals,
    readAt: Math.floor(Date.now() / 1000),
  };
}

/**
 * Never cached. Ember reaches this data through `store.query`, which always
 * hits the server — right for a searched, filtered, sorted, paged list where
 * a cached page would show a stale row set, and where mutations made
 * elsewhere in the app land without this list observing them.
 *
 * @public
 */
export const getFilteredNewslettersQueryOptions = (
  workspaceId: number,
  params: NewsletterListParams,
  qc: QueryClient,
) =>
  queryOptions({
    queryKey: newsletterKeys.list(workspaceId, params),
    queryFn: () => getFilteredNewsletters(workspaceId, params, qc),
    staleTime: 0,
  });

interface UseFilteredNewslettersOptions {
  workspaceId?: number;
  params: NewsletterListParams;
  queryConfig?: QueryConfig<typeof getFilteredNewslettersQueryOptions>;
}

/**
 * The Broadcasts newsletters tab's list read.
 *
 * Non-suspense on purpose. The Ember page degrades a failed list fetch to an
 * empty tab rather than routing to the app-wide error substate, and it tells
 * "not loaded" apart from "loaded and empty" when deciding to show the
 * welcome state. Both need the error and pending flags in hand, which a
 * suspense read hands to a boundary instead.
 *
 * @public
 */
export const useFilteredNewsletters = ({
  workspaceId: overrideWorkspaceId,
  params,
  queryConfig,
}: UseFilteredNewslettersOptions) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  return useQuery({
    ...getFilteredNewslettersQueryOptions(workspaceId, params, qc),
    ...queryConfig,
  });
};

/** One page of the newsletter catalog plus the server's pagination meta. */
export interface NewsletterCatalogPage {
  newsletters: Newsletter[];
  /** Rows matching the scope across all pages, from the pagination meta. */
  total: number;
  /** The server's page size (the endpoint fixes it; the response reports it). */
  size: number;
}

/**
 * One server page of workspace newsletters, optionally name-searched
 * (case-insensitive substring; `page` is 0-based). Each row is sideloaded
 * into its detail slot so a picker's by-id name lookup resolves from cache.
 *
 * @public
 */
export async function getNewslettersPage(
  workspaceId: number,
  { page, search }: NewsletterCatalogScope & { page: number },
  qc: QueryClient,
): Promise<NewsletterCatalogPage> {
  const query: NewslettersListQuery = { page };
  if (search != null) {
    query.search = search;
  }
  const raw = await hydraUiApi.newsletters.list({
    params: { path: { environment_id: String(workspaceId) }, query },
  });
  const newsletters = sideloadNewsletters(workspaceId, raw.newsletters, qc);
  return {
    newsletters,
    total: raw.meta?.pagination?.total ?? newsletters.length,
    size: raw.meta?.pagination?.size ?? newsletters.length,
  };
}

/**
 * The server-paged newsletter catalog for pickers. The first page loads on
 * mount; callers pull further pages with `fetchNextPage`. A page shorter than
 * the server's page size (or empty) ends the catalog — the same two stop
 * conditions the legacy editor derives from the pagination meta. A changed
 * search term is a new catalog starting from page 0; the previous term's
 * pages stay visible while the first page loads, so a keystroke doesn't blank
 * the picker.
 *
 * @public
 */
export function useNewsletterCatalog(workspaceId: number, scope: NewsletterCatalogScope = {}) {
  const qc = useQueryClient();
  return useInfiniteQuery({
    queryKey: newsletterCatalogKey(workspaceId, scope),
    queryFn: ({ pageParam }) => getNewslettersPage(workspaceId, { page: pageParam, ...scope }, qc),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.newsletters.length === 0 || lastPage.newsletters.length < lastPage.size ? undefined : lastPageParam + 1,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/** @public */
export async function getNewsletter(workspaceId: number, newsletterId: string, qc: QueryClient): Promise<Newsletter> {
  const raw = await hydraUiApi.newsletters.get({
    params: { path: { environment_id: String(workspaceId), id: newsletterId } },
  });
  if (raw.newsletter == null) {
    throw new Error(`newsletter ${newsletterId} not found`);
  }
  const parsed = parseNewsletter(raw.newsletter);
  qc.setQueryData(newsletterKeys.detail(workspaceId, parsed.id), parsed);
  return parsed;
}

/** @public */
export const getNewsletterQueryOptions = (workspaceId: number, newsletterId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: newsletterKeys.detail(workspaceId, newsletterId),
    queryFn: () => getNewsletter(workspaceId, newsletterId, qc),
    staleTime: STALE_TIME,
  });

interface UseNewsletterOptionalOptions {
  workspaceId: number;
  id: string;
  enabled?: boolean;
}

/**
 * Non-suspense, conditionally-enabled by-id hook (see `useSnippetOptional`).
 *
 * @public
 */
export const useNewsletterOptional = ({ workspaceId, id, enabled = true }: UseNewsletterOptionalOptions) => {
  const qc = useQueryClient();
  const active = enabled && workspaceId > 0 && id !== '';
  return useQuery({
    queryKey: active ? newsletterKeys.detail(workspaceId, id) : ['newsletter', 'inactive'],
    queryFn: active ? () => getNewsletter(workspaceId, id, qc) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
};

/* ------------------------------------------------------------------------- */
/* getNewsletterSchedule                                                      */
/* ------------------------------------------------------------------------- */

/** A newsletter's schedule: when it is set to send, and when it was first set to. */
export type NewsletterSchedule = NewslettersNewsletterScheduleResponse['schedule'];

async function getNewsletterSchedule(workspaceId: number, newsletterId: string): Promise<NewsletterSchedule> {
  const raw = await hydraUiApi.newsletters.getSchedule({
    params: { path: { environment_id: String(workspaceId), id: newsletterId } },
  });
  return raw.schedule;
}

/**
 * Cached for a minute, like the other per-item catalog reads: a list asks for one per row, and a
 * schedule changes only when its send is edited.
 */
const getNewsletterScheduleQueryOptions = (workspaceId: number, newsletterId: string) =>
  queryOptions({
    queryKey: newsletterScheduleKey(workspaceId, newsletterId),
    queryFn: () => getNewsletterSchedule(workspaceId, newsletterId),
    staleTime: STALE_TIME,
  });

interface UseNewsletterScheduleOptions {
  workspaceId?: number;
  newsletterId: string;
}

/** Non-suspense, so a row can show its own loading state while the rest of the list is up. */
export const useNewsletterSchedule = ({
  workspaceId: overrideWorkspaceId,
  newsletterId,
}: UseNewsletterScheduleOptions) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  return useQuery(getNewsletterScheduleQueryOptions(workspaceId, newsletterId));
};

/* ------------------------------------------------------------------------- */
/* getNewsletterMetric                                                        */
/* ------------------------------------------------------------------------- */

/** A newsletter's delivery metrics: all-time totals, and a daily series for each counter. */
export type NewsletterMetric = Metric;

async function getNewsletterMetric(workspaceId: number, newsletterId: string): Promise<NewsletterMetric> {
  const raw = await hydraUiApi.newsletters.getMetrics({
    params: { path: { environment_id: String(workspaceId), id: newsletterId }, query: { period: 'days' } },
  });
  return raw.metric;
}

interface UseNewsletterMetricOptions {
  workspaceId?: number;
  newsletterId: string;
}

/**
 * Non-suspense, so a row fills in its metrics on its own. Fresh for 30 seconds, as Ember's metrics
 * service hands a row the result it read in the last 30 seconds rather than asking again.
 */
export const useNewsletterMetric = ({ workspaceId: overrideWorkspaceId, newsletterId }: UseNewsletterMetricOptions) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  return useQuery({
    queryKey: newsletterMetricKey(workspaceId, newsletterId),
    queryFn: () => getNewsletterMetric(workspaceId, newsletterId),
    staleTime: 30_000,
  });
};

/** @public */
export async function getNewsletterRecipientCounts(
  workspaceId: number,
  newsletter: Pick<Newsletter, 'id' | 'sent_at'>,
) {
  const params = { path: { environment_id: String(workspaceId), id: newsletter.id } };
  if (isSent(newsletter)) {
    const raw = await hydraUiApi.newsletters.getCounts({ params });
    return {
      total: raw.recipient_count,
      sto: 0,
      deduped: raw.deduped_count,
      created: raw.created_count,
      timezoneMisconfigured: 0,
    };
  }
  const raw = await hydraUiApi.newsletters.getPreviewRecipientCounts({ params });
  return {
    total: raw.recipient_count,
    sto: raw.sto_recipient_count,
    deduped: 0,
    created: 0,
    timezoneMisconfigured: raw.timezone_misconfigured_count,
  };
}

/** @public */
export type NewsletterRecipientCounts = Awaited<ReturnType<typeof getNewsletterRecipientCounts>>;

/** @public */
export const getNewsletterRecipientCountsQueryOptions = (
  workspaceId: number,
  newsletter: Pick<Newsletter, 'id' | 'sent_at'>,
) =>
  queryOptions({
    queryKey: [...newsletterKeys.detail(workspaceId, newsletter.id), 'recipient-counts', isSent(newsletter)],
    queryFn: () => getNewsletterRecipientCounts(workspaceId, newsletter),
    staleTime: 0,
  });

type UseNewsletterRecipientCountsOptions = {
  workspaceId?: number;
  newsletter: Pick<Newsletter, 'id' | 'sent_at'>;
  queryConfig?: QueryConfig<typeof getNewsletterRecipientCountsQueryOptions>;
};

/** @public */
export function useNewsletterRecipientCounts({
  workspaceId: override,
  newsletter,
  queryConfig,
}: UseNewsletterRecipientCountsOptions) {
  const workspaceId = override ?? requireWorkspaceId();
  return useQuery({ ...getNewsletterRecipientCountsQueryOptions(workspaceId, newsletter), ...queryConfig });
}

/* ------------------------------------------------------------------------- */
/* getNewsletterSendStatus                                                    */
/* ------------------------------------------------------------------------- */

/** How far a newsletter's send has got: what's queued, and until when it can still be cancelled. */
export type NewsletterSendStatus = NewslettersSendStatusResponse;

async function getNewsletterSendStatus(workspaceId: number, newsletterId: string): Promise<NewsletterSendStatus> {
  return hydraUiApi.newsletters.getSendStatus({
    params: { path: { environment_id: String(workspaceId), id: newsletterId } },
  });
}

/** How often Ember re-reads a send's status: every 5 seconds while it can be cancelled, every minute while paced. */
const SEND_STATUS_POLL_MS = 5_000;
const PACED_SEND_STATUS_POLL_MS = 60_000;

/** Whether a status says the send can be cancelled until later than it was read. */
export function isCancellableAfter(status: NewsletterSendStatus | undefined, readAtSeconds: number): boolean {
  const timestamp = status?.timestamp;
  return timestamp != null && readAtSeconds < timestamp;
}

function sendStatusPollInterval(status: NewsletterSendStatus | undefined, readAtMs: number): number | false {
  if (isCancellableAfter(status, Math.floor(readAtMs / 1000))) {
    return SEND_STATUS_POLL_MS;
  }
  return status?.pacing == null ? false : PACED_SEND_STATUS_POLL_MS;
}

interface UseNewsletterSendStatusOptions {
  workspaceId?: number;
  newsletterId: string;
  enabled: boolean;
}

/**
 * Non-suspense. Polls as Ember's `pollForSendStatusTask` does, while the send can still be
 * cancelled or is paced, and stops once neither holds.
 */
export const useNewsletterSendStatus = ({
  workspaceId: overrideWorkspaceId,
  newsletterId,
  enabled,
}: UseNewsletterSendStatusOptions) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  return useQuery({
    queryKey: newsletterSendStatusKey(workspaceId, newsletterId),
    queryFn: () => getNewsletterSendStatus(workspaceId, newsletterId),
    enabled,
    refetchInterval: (query) => sendStatusPollInterval(query.state.data, query.state.dataUpdatedAt),
  });
};

/** @public */
export function useNewsletterOverviewSendStatus(id: string) {
  const workspaceId = requireWorkspaceId();
  const newsletter = useNewsletterOptional({ workspaceId, id });
  const row = newsletter.data;
  const rateLimitEnabled = (row?.rate_limit_email_rate ?? 0) > 0 && (row?.rate_limit_time_period ?? 0) > 0;
  const enabled = row != null && (row.sending || (!rateLimitEnabled && !row.tz_match_enabled));
  const status = useNewsletterSendStatus({ workspaceId, newsletterId: id, enabled });
  const cancellableUntilIsInFuture = isCancellableAfter(status.data, Math.floor(Date.now() / 1000));
  return {
    estimatedCompletionTimestamp: status.data?.timestamp,
    enqueuedCount: status.data?.enqueued_count,
    pacing: status.data?.pacing,
    canBeCancelled: row?.sending === true || cancellableUntilIsInFuture,
    cancellableUntilIsInFuture,
  };
}

/** Send counts per bucket over `start..end` (unix seconds) for a newsletter's single send. */
export async function getNewsletterSendTiming(
  workspaceId: number,
  newsletterId: string,
  query: { start: number; end: number },
  signal?: AbortSignal,
): Promise<NewslettersNewsletterSendTiming> {
  const response = await hydraUiApi.newsletters.getSendTiming({
    params: { path: { environment_id: String(workspaceId), id: newsletterId }, query },
    signal,
  });
  return response.send_timing;
}

export async function getNewsletterTemplates(
  workspaceId: number,
  newsletterId: string,
): Promise<NewslettersNewsletterTemplatesResponse> {
  return hydraUiApi.newsletters.getTemplates({
    params: { path: { environment_id: String(workspaceId), id: newsletterId } },
  });
}

export const getNewsletterTemplatesQueryOptions = (workspaceId: number, newsletterId: string) =>
  queryOptions({
    queryKey: [...newsletterKeys.detail(workspaceId, newsletterId), 'templates'],
    queryFn: () => getNewsletterTemplates(workspaceId, newsletterId),
    staleTime: 0,
  });

export async function getNewsletterPacingPreview(
  workspaceId: number,
  newsletterId: string,
): Promise<NewslettersPacingResponse | null> {
  try {
    return await hydraUiApi.newsletters.getPacingPreview({
      params: { path: { environment_id: String(workspaceId), id: newsletterId } },
    });
  } catch {
    return null;
  }
}

type UseNewsletterPacingPreviewOptions = {
  newsletterId: string;
  enabled: boolean;
};

export function useNewsletterPacingPreview({ newsletterId, enabled }: UseNewsletterPacingPreviewOptions) {
  const workspaceId = requireWorkspaceId();
  return useQuery({
    queryKey: [...newsletterKeys.detail(workspaceId, newsletterId), 'pacing-preview'],
    queryFn: () => getNewsletterPacingPreview(workspaceId, newsletterId),
    staleTime: 0,
    enabled,
  });
}

const NEWSLETTER_REPORT_QUERY_CONFIG = {
  staleTime: 30_000,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
} as const;

export async function getNewsletterMetrics(
  workspaceId: number,
  newsletterId: string,
  period: NewsletterMetricsPeriod = 'days',
) {
  return hydraUiApi.newsletters.getMetrics({
    params: { path: { environment_id: String(workspaceId), id: newsletterId }, query: { period } },
  });
}

export const getNewsletterMetricsQueryOptions = (
  workspaceId: number,
  newsletterId: string,
  period: NewsletterMetricsPeriod = 'days',
) =>
  queryOptions({
    queryKey: newsletterMetricsKey(workspaceId, newsletterId, period),
    queryFn: () => getNewsletterMetrics(workspaceId, newsletterId, period),
    ...NEWSLETTER_REPORT_QUERY_CONFIG,
  });

/** All-time tracked links for the whole newsletter; the server fixes the window from its send. */
export const getNewsletterTrackedLinksQueryOptions = (workspaceId: number, newsletterId: string) =>
  queryOptions({
    queryKey: newsletterTrackedLinksKey(workspaceId, newsletterId),
    queryFn: () =>
      hydraUiApi.newsletters.getTrackedLinkMetric({
        params: { path: { environment_id: String(workspaceId), id: newsletterId } },
      }),
    ...NEWSLETTER_REPORT_QUERY_CONFIG,
  });

/** One variation's tracked links over the window the caller builds (Ember sends the newsletter's). */
export const getTemplateTrackedLinksQueryOptions = (
  workspaceId: number,
  newsletterId: string,
  templateId: string,
  query: TemplateTrackedLinksQuery,
) =>
  queryOptions({
    queryKey: templateTrackedLinksKey(workspaceId, newsletterId, templateId, query),
    queryFn: () =>
      hydraUiApi.trackedLinkMetrics.getTemplatesTrackedLinkMetric({
        params: { path: { environment_id: String(workspaceId), template_id: templateId }, query },
      }),
    ...NEWSLETTER_REPORT_QUERY_CONFIG,
  });

export async function getNewsletterTemplateMetrics(workspaceId: number, newsletterId: string) {
  return hydraUiApi.newsletters.getTemplateMetrics({
    params: { path: { environment_id: String(workspaceId), id: newsletterId } },
  });
}

export const getNewsletterTemplateMetricsQueryOptions = (workspaceId: number, newsletterId: string) =>
  queryOptions({
    queryKey: newsletterTemplateMetricsKey(workspaceId, newsletterId),
    queryFn: () => getNewsletterTemplateMetrics(workspaceId, newsletterId),
    ...NEWSLETTER_REPORT_QUERY_CONFIG,
  });

/**
 * Tracked responses (in-app and inbox only), per variation. The server fixes the window from the
 * send, so the query carries no params; one response serves every variation.
 */
export const getNewsletterTrackedResponseMetricsQueryOptions = (workspaceId: number, newsletterId: string) =>
  queryOptions({
    queryKey: newsletterTrackedResponseMetricsKey(workspaceId, newsletterId),
    queryFn: () =>
      hydraUiApi.newsletters.getTrackedResponseMetrics({
        params: { path: { environment_id: String(workspaceId), id: newsletterId } },
      }),
    ...NEWSLETTER_REPORT_QUERY_CONFIG,
  });
